import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LayoutGrid, List } from 'lucide-react';
import {
  RAGBadge,
  RAGStatus,
  MachineLifecycleBadge,
  MachineLifecycleStatus,
  MachinePicklistsCard,
  RefillHistoryCard,
} from '../../components/machines';
import { ContentPageHeader } from '@/components/global';
import { SlotGrid, SlotProductList } from '../../components/slots';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { apiFetch } from '../../lib/api';
import { messageForFailedResponse } from '../../lib/formatApiError';
import { usePermission } from '../../hooks/usePermission';
import { parseMachineDetailTab } from '../../lib/entityPaths';
import { machinePicklistHeaderActionLabel } from '../../lib/machinePicklist';
import { useMachinePicklistAction } from '../../hooks/useMachinePicklistAction';
import { isCatalogProductActive } from '../../lib/productCatalog';
import { calculateSlotRagStatus } from '../../lib/rag';
import { cn } from '../../lib/utils';

interface SlotData {
  id: string;
  slotNumber: number;
  slotLabel: string | null;
  productId: string | null;
  /** Present when API includes slot product join */
  product?: {
    id: string;
    name: string;
    sku: string;
    imageUrl?: string | null;
    media?: Array<{ url: string; mediaType: string }>;
    isActive?: boolean | null;
    status?: string | null;
    deletedAt?: string | Date | null;
  } | null;
  productName?: string;
  currentQuantity: number;
  maxCapacity: number;
  ragStatus: { status: RAGStatus; percentageFilled: number };
}

interface MachineDetailData {
  machine: {
    id: string;
    name: string;
    serialNumber: string;
    modelType: string | null;
    slotCount: number;
    ragGreenThreshold?: number;
    ragAmberThreshold?: number;
    lastHeartbeatAt: string | null;
    lastSaleAt: string | null;
    lifecycleStatus: MachineLifecycleStatus;
    plannedSwitchDate: string | null;
    venue?: { id: string; name: string };
    slots?: SlotData[];
  };
  ragStatus: { status: RAGStatus; percentageFilled: number; currentStock: number; maxCapacity: number };
  isOnline: boolean;
}

export function MachineDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseMachineDetailTab(searchParams.get('tab'));
  const [machine, setMachine] = useState<MachineDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planogramView, setPlanogramView] = useState<'list' | 'grid'>('list');
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const canWriteMachines = usePermission('machines.write');
  const unmanaged = machine?.machine.lifecycleStatus === 'UNMANAGED';
  const picklistAction = useMachinePicklistAction(id, Boolean(unmanaged));
  const picklistHeaderLabel = machinePicklistHeaderActionLabel(picklistAction.action);

  const fetchMachine = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const response = await apiFetch(`/api/v1/machines/${id}`);

      if (!response.ok) {
        if (silent) {
          throw new Error(await messageForFailedResponse(response, 'Failed to refresh machine'));
        }
        if (response.status === 404) {
          setError('Machine not found');
        } else {
          throw new Error('Failed to fetch machine');
        }
        return;
      }

      const data: MachineDetailData = await response.json();
      setMachine(data);
    } catch (err) {
      if (silent) throw err;
      setError('Failed to load machine details');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  const syncFromCloud = useCallback(async () => {
    if (!id) return;
    setSyncing(true);
    setSyncError(null);
    try {
      const response = await apiFetch(`/api/v1/machines/${id}/cloud-sync`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(await messageForFailedResponse(response, 'Failed to sync from Cloud'));
      }
      await fetchMachine(true);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Failed to sync from Cloud');
    } finally {
      setSyncing(false);
    }
  }, [id, fetchMachine]);

  useEffect(() => {
    fetchMachine();
  }, [fetchMachine]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <div className="size-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Loading machine details…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !machine) {
    return (
      <div className="min-h-screen bg-background">
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
            <p className="text-sm text-destructive">{error || 'Machine not found'}</p>
            <Button variant="outline" onClick={() => navigate('/machines')}>
              Back to Machines
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const slots = machine.machine.slots || [];
  const slotsWithProduct = slots.filter((s) => s.productId).length;
  const slotsWithStockButNoProduct = slots.filter(
    (s) => !s.productId && s.currentQuantity > 0
  ).length;
  const slotsWithInactiveProduct = slots.filter(
    (s) => Boolean(s.productId) && !isCatalogProductActive(s.product)
  ).length;

  const slotRagThresholds = {
    greenThreshold: machine.machine.ragGreenThreshold ?? 80,
    amberThreshold: machine.machine.ragAmberThreshold ?? 60,
  };

  const slotsWithRAG = slots.map((slot) => ({
    ...slot,
    productName:
      slot.product?.name?.trim() || slot.product?.sku || slot.productName,
    ragStatus:
      slot.ragStatus ??
      calculateSlotRagStatus(slot.currentQuantity, slot.maxCapacity, slotRagThresholds),
  }));

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-6">
        <ContentPageHeader
          back={{ to: '/machines', label: 'Machines' }}
          title={machine.machine.name}
          description={machine.machine.venue?.name}
          actionsClassName="sm:max-w-none"
          actions={
            <>
              <MachineLifecycleBadge status={machine.machine.lifecycleStatus ?? 'LIVE'} />
              <RAGBadge
                status={machine.ragStatus.status}
                percentageFilled={machine.ragStatus.percentageFilled}
                showPercentage
                size="lg"
              />
              {machine.machine.lifecycleStatus === 'LIVE' ? (
                <Badge variant={machine.isOnline ? 'default' : 'secondary'} className="text-sm">
                  {machine.isOnline ? 'Online' : 'Offline'}
                </Badge>
              ) : null}
              {canWriteMachines ? (
                <Button variant="outline" disabled={syncing} onClick={() => void syncFromCloud()}>
                  {syncing ? 'Syncing…' : 'Sync from Cloud'}
                </Button>
              ) : null}
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="outline" onClick={() => navigate(`/machines/${id}/edit`)}>
                  Edit
                </Button>
                {!picklistAction.loading && picklistHeaderLabel ? (
                  <Button
                    disabled={picklistAction.generating}
                    onClick={() => {
                      if (picklistAction.action === 'continue') {
                        picklistAction.continuePicklist();
                        return;
                      }
                      void picklistAction.generate();
                    }}
                  >
                    {picklistAction.generating ? 'Generating…' : picklistHeaderLabel}
                  </Button>
                ) : null}
              </div>
            </>
          }
        />

        {syncError ? (
          <p className="text-sm text-destructive" role="alert">
            {syncError}
          </p>
        ) : null}

        {picklistAction.error ? (
          <p className="text-sm text-destructive" role="alert">
            {picklistAction.error}
          </p>
        ) : null}

        {machine.machine.lifecycleStatus === 'UNMANAGED' ? (
          <div
            className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
            role="status"
          >
            <p className="font-medium">This machine is unmanaged in Express</p>
            <p className="mt-1 text-amber-900/90 dark:text-amber-100/90">
              It is hidden from lists, stock, and refills. Cloud still syncs it. Set a Cloud
              lifecycle (Draft, Scheduled, Live, or Archived) on Edit to bring it back.
            </p>
          </div>
        ) : null}

        {slotsWithStockButNoProduct > 0 && (
          <div
            className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
            role="status"
          >
            <p className="font-medium">Slots show stock but no linked catalog product</p>
            <p className="mt-1 text-amber-900/90 dark:text-amber-100/90">
              {slotsWithStockButNoProduct} slot{slotsWithStockButNoProduct === 1 ? '' : 's'} have
              inventory levels without a product assignment. That is why “Slots used” can read 0 and
              picklist generation may fail. Re-run a catalog import that includes machines (the
              export sometimes lists SKUs only on slots, not in the main product list), or assign
              products to those slots.
            </p>
          </div>
        )}

        {slotsWithInactiveProduct > 0 && (
          <div
            className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
            role="status"
          >
            <p className="font-medium">Inactive catalog products on this machine</p>
            <p className="mt-1 text-amber-900/90 dark:text-amber-100/90">
              {slotsWithInactiveProduct} slot{slotsWithInactiveProduct === 1 ? '' : 's'} still
              reference a deleted or inactive product. Those slots are skipped when generating a
              picklist. Assign an active product to restock them.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{Math.round(machine.ragStatus.percentageFilled)}%</div>
              <div className="text-sm text-muted-foreground">Stock Level</div>
              <div className="h-2 bg-muted rounded-full mt-2 overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-[width] duration-300 ease-out',
                    machine.ragStatus.status === 'GREEN' && 'bg-rag-good',
                    machine.ragStatus.status === 'AMBER' && 'bg-rag-warning',
                    machine.ragStatus.status === 'RED' && 'bg-rag-critical'
                  )}
                  style={{ width: `${machine.ragStatus.percentageFilled}%` }}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">
                {slotsWithProduct}/{machine.machine.slotCount}
              </div>
              <div className="text-sm text-muted-foreground">Slots with product</div>
              {slotsWithStockButNoProduct > 0 && (
                <div className="text-xs text-muted-foreground mt-2">
                  {slotsWithStockButNoProduct} slot{slotsWithStockButNoProduct === 1 ? '' : 's'} with
                  stock but no product link
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{formatDate(machine.machine.lastHeartbeatAt)}</div>
              <div className="text-sm text-muted-foreground">Last Heartbeat</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{formatDate(machine.machine.lastSaleAt)}</div>
              <div className="text-sm text-muted-foreground">Last Sale</div>
            </CardContent>
          </Card>
        </div>

        <Tabs
          value={tab}
          onValueChange={(value) => {
            const next = parseMachineDetailTab(value);
            const params = new URLSearchParams(searchParams);
            if (next === 'overview') {
              params.delete('tab');
            } else {
              params.set('tab', next);
            }
            setSearchParams(params, { replace: true });
          }}
          className="space-y-4"
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="picklists">Picklists</TabsTrigger>
            <TabsTrigger value="refills">Refills</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card>
              <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle>Planogram</CardTitle>
                {slotsWithRAG.length > 0 ? (
                  <Tabs
                    value={planogramView}
                    onValueChange={(value) => setPlanogramView(value as 'list' | 'grid')}
                  >
                    <TabsList>
                      <TabsTrigger value="list" className="gap-1.5">
                        <List className="size-3.5" aria-hidden />
                        List
                      </TabsTrigger>
                      <TabsTrigger value="grid" className="gap-1.5">
                        <LayoutGrid className="size-3.5" aria-hidden />
                        Grid
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                ) : null}
              </CardHeader>
              <CardContent className={planogramView === 'list' && slotsWithRAG.length > 0 ? 'p-0' : undefined}>
                {slotsWithRAG.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                    <p className="text-sm font-medium text-foreground">No slots configured</p>
                    <p className="text-sm text-muted-foreground">
                      Configure slot assignments for this machine to track stock levels.
                    </p>
                  </div>
                ) : planogramView === 'list' ? (
                  <SlotProductList slots={slotsWithRAG} thresholds={slotRagThresholds} />
                ) : (
                  <SlotGrid slots={slotsWithRAG} columns={5} />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="picklists">
            {id ? (
              <MachinePicklistsCard
                machineId={id}
                unmanaged={machine.machine.lifecycleStatus === 'UNMANAGED'}
                action={picklistAction.action}
                latest={picklistAction.latest}
                activeRefill={picklistAction.activeRefill}
                generating={picklistAction.generating}
                onNewPicklist={() => void picklistAction.generate()}
                onContinue={picklistAction.continuePicklist}
              />
            ) : null}
          </TabsContent>

          <TabsContent value="refills">
            {id ? <RefillHistoryCard machineId={id} /> : null}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default MachineDetail;
